import {
  ELicenseType,
  IFamilyContext,
  SubscriptionStatus,
  User,
  UserSubscription,
  UserType,
} from 'types'

export function formatCardExpiry(expMonth: number | null, expYear: number | null): string {
  const month = String(expMonth ?? '').padStart(2, '0')
  const year = String(expYear ?? '').slice(-2)
  return `${month}/${year}`
}

/** Family plans share seats — paid or partner-funded alike. */
export function isFamilyPlan(plan?: string | null): boolean {
  return plan === 'caregiver_family' || plan === 'caregiver_family_funded'
}

/** Activated by a partner-funded code: no card, no renewal, ends on currentPeriodEnd. */
export function isPartnerFunded(subscription?: UserSubscription | null): boolean {
  if (!subscription) return false
  return (
    subscription.billingType === 'partner_funded' || subscription.plan === 'caregiver_family_funded'
  )
}

/**
 * Seats a Family owner can still hand out; pending invites hold a seat too. Anyone who is not the
 * owner of a Family plan has nothing to give — their invitees subscribe on their own.
 */
export function getFamilySeatsLeft(familyContext?: IFamilyContext | null): number {
  if (!familyContext || familyContext.role !== 'owner') return 0
  const { maxMembers, membersActiveCount, membersInvitedCount } = familyContext
  return Math.max(0, maxMembers - membersActiveCount - membersInvitedCount)
}

/** Caregiver has an active entitlement: status active|trialing and current period still in the future. */
export function isSubscriptionActive(subscription?: UserSubscription | null): boolean {
  if (!subscription) return false
  const activeStatus =
    subscription.status === SubscriptionStatus.Active ||
    subscription.status === SubscriptionStatus.Trialing
  if (!activeStatus) return false
  if (!subscription.currentPeriodEnd) return false
  return new Date(subscription.currentPeriodEnd).getTime() > Date.now()
}

/**
 * Whether this user's access depends on a paid subscription at all.
 *
 * Family Caregivers always pay for their own access. Trainees only do when they bought an
 * individual licence — a trainee sitting on an organization's access code must never be gated,
 * so the check is on `licenseType`, not on the role.
 */
export function isSubscriptionGated(user?: User | null): boolean {
  if (!user) return false
  if (user.currentType === UserType.CAREGIVER) return true
  return user.currentType === UserType.TRAINEE && user.licenseType === ELicenseType.individual
}

/**
 * Whether the logged-in user may access subscription-gated routes.
 * Uses the backend entitlement flag when present; falls back to lastSubscription for legacy
 * sessions. Users whose access does not depend on a subscription always pass.
 */
const PAYMENT_RECOVERY_STATUSES = new Set([
  SubscriptionStatus.PastDue,
  SubscriptionStatus.Unpaid,
  SubscriptionStatus.Incomplete,
])

/** Past Due, Unpaid, and Incomplete keep the account and open My Subscription. Canceled does not. */
export function isPaymentRecovery(user?: User | null): boolean {
  const status = user?.lastSubscription?.status
  return !!status && PAYMENT_RECOVERY_STATUSES.has(status)
}

export function hasActiveEntitlement(user?: User | null): boolean {
  if (!isSubscriptionGated(user) || !user) {
    return true
  }
  if (typeof user.hasActiveSubscription === 'boolean') {
    return user.hasActiveSubscription
  }
  return isSubscriptionActive(user.lastSubscription)
}

/** Full access, or the recovery shell where only My Subscription is available. */
export function canUseSubscriptionShell(user?: User | null): boolean {
  return hasActiveEntitlement(user) || isPaymentRecovery(user)
}
